import { A11yModule } from '@angular/cdk/a11y'
import { Component, computed, input, linkedSignal, output } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { DateCalendarComponent } from '../date-calendar/date-calendar.component'
import { PickerMode } from '../date-time-picker.types'
import { withTimeOfDay } from '../time-of-day'
import { TimeStepperComponent } from '../time-stepper/time-stepper.component'

const DIALOG_LABELS: Record<PickerMode, string> = {
  date: 'Výber dátumu',
  datetime: 'Výber dátumu a času',
  time: 'Výber času',
}

/** A date with no time of its own takes noon: it cannot cross a day boundary when the caller moves it to a zone. */
const NOON = 12

/**
 * The picker's overlay surface: `seiri-date-calendar` and `seiri-time-stepper` composed by `mode`, plus Potvrdiť.
 * `[seiriDateTimePicker]` opens it; nothing else should need to.
 *
 * Zone-free: it reads and emits browser-local wall-clock `Date`s, and the caller converts to the premise's zone. The
 * input `value` is never mutated; `picked` always emits a new `Date`, never `undefined`.
 *
 * - **date** emits on the day click. Only the calendar day changes: the time of day is the value's, or 12:00 when
 *   there is no value.
 * - **datetime** emits the chosen day plus the stepper's time on Potvrdiť, which stays disabled until a day is chosen.
 *   With no value, a chosen day starts at 12:00.
 * - **time** changes only hours and minutes and emits on Potvrdiť. The date part is the value's, or today's.
 */
@Component({
  selector: 'seiri-date-time-picker-panel',
  imports: [A11yModule, MatButtonModule, DateCalendarComponent, TimeStepperComponent],
  templateUrl: './date-time-picker-panel.component.html',
  styleUrl: './date-time-picker-panel.component.css',
  host: { class: 'seiri-date-time-picker-panel' },
})
export class DateTimePickerPanelComponent {
  readonly mode = input<PickerMode>('datetime')
  readonly value = input<Date | null | undefined>(null)
  readonly min = input<Date | null | undefined>(null)
  readonly max = input<Date | null | undefined>(null)
  readonly startAt = input<Date | null | undefined>(null)
  readonly startView = input<'days' | 'years'>('days')
  readonly picked = output<Date>()

  protected readonly dialogLabel = computed(() => DIALOG_LABELS[this.mode()])
  protected readonly showsCalendar = computed(() => this.mode() !== 'time')
  protected readonly showsTime = computed(() => this.mode() !== 'date')

  /** The value being edited, before Potvrdiť. */
  protected readonly draft = linkedSignal<Date | null>(() => this.value() ?? null)
  /** Whether the draft's day was chosen, rather than taken from today by a first step of the time. */
  private readonly dayChosen = linkedSignal(() => !!this.value())

  protected readonly calendarSelected = computed(() => (this.dayChosen() ? this.draft() : null))
  protected readonly canConfirm = computed(() =>
    this.mode() === 'time' ? this.draft() !== null : this.dayChosen() && this.draft() !== null,
  )

  protected pickDay(day: Date): void {
    const current = this.draft()
    const next = withTimeOfDay(day, current?.getHours() ?? NOON, current?.getMinutes() ?? 0)
    if (this.mode() === 'date') {
      this.picked.emit(next)
      return
    }
    this.draft.set(next)
    this.dayChosen.set(true)
  }

  protected confirm(): void {
    const draft = this.draft()
    if (draft && this.canConfirm()) this.picked.emit(draft)
  }
}
